/**
 * TemplateReviewModal.tsx
 * Modal for reviewing and applying template updates to user instruments
 * 
 * Allows users to:
 * - Review instruments with outdated templates
 * - Accept/reject changes field by field
 * - Bulk accept/reject operations
 * - Save progress and continue later
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, 
  Button, Card, CardBody, CardHeader, Input,
  Switch, ButtonGroup
} from '@enfomentis/ui';
import type { Instrument } from '@/types/schema/Instrument';
import { instrumentService } from '@/services/InstrumentService';

/**
 * Interface for field change data
 */
interface FieldChange {
  fieldName: string;
  fieldLabel: string;
  currentValue: string | number;
  templateValue: string | number;
  isCustomized: boolean;
}

/**
 * Interface for instrument template changes
 */
interface InstrumentTemplateChanges {
  instrumentId: string;
  instrumentName: string;
  hasTemplate: boolean;
  templateExists: boolean;
  fieldChanges: FieldChange[];
  hasChanges: boolean;
}

/**
 * Props for TemplateReviewModal component
 */
interface TemplateReviewModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Instruments that need template updates */
  outdatedInstruments?: Instrument[];
  /** Count of outdated instruments for display */
  outdatedCount?: number;
  /** Callback when instruments are updated (refresh parent data) */
  onInstrumentsUpdated?: () => void;
}

/**
 * Enhanced Template Review Modal Component
 * 
 * Displays detailed template comparisons with field-level Accept/Reject controls,
 * bulk operations, and persistent session management.
 */
export const TemplateReviewModal: React.FC<TemplateReviewModalProps> = ({
  isOpen,
  onClose,
  outdatedInstruments = [],
  onInstrumentsUpdated
}) => {
  // ===== STATE MANAGEMENT =====
  const [instrumentChanges, setInstrumentChanges] = useState<InstrumentTemplateChanges[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Track field decisions: instrumentId -> fieldName -> accept(true)/reject(false)
  const [fieldDecisions, setFieldDecisions] = useState<Record<string, Record<string, boolean>>>({});
  
  // Track which instruments have been processed/committed
  const [processedInstruments, setProcessedInstruments] = useState<string[]>([]);
  
  // Track which instruments are currently being processed (for animation)
  const [processingInstruments, setProcessingInstruments] = useState<Set<string>>(new Set());
  

  const loadInstrumentChanges = useCallback(async () => {
    setLoading(true);
    try {
      const changes: InstrumentTemplateChanges[] = [];
      
      for (const instrument of outdatedInstruments) {
        const changeData = await instrumentService.getInstrumentTemplateChanges(instrument);
        if (changeData.hasChanges) {
          changes.push(changeData);
        }
      }
      
      setInstrumentChanges(changes);
      
      // Initialize field decisions with default "Accept" for all fields
      const initialDecisions: Record<string, Record<string, boolean>> = {};
      changes.forEach(change => {
        const instrumentDecisions: Record<string, boolean> = {};
        change.fieldChanges.forEach(field => {
          instrumentDecisions[field.fieldName] = true; // Default to Accept
        });
        initialDecisions[change.instrumentId] = instrumentDecisions;
      });
      setFieldDecisions(initialDecisions);
      
      // Load any existing session state
      loadSessionState();
      
    } catch (error) {
      console.error('Failed to load instrument changes:', error);
    } finally {
      setLoading(false);
    }
  }, [outdatedInstruments]);

  // ===== LOAD INSTRUMENT CHANGES =====
  useEffect(() => {
    if (isOpen && outdatedInstruments.length > 0) {
      loadInstrumentChanges();
    }
  }, [isOpen, outdatedInstruments, loadInstrumentChanges]);

  // ===== SESSION PERSISTENCE =====
  const loadSessionState = () => {
    const session = instrumentService.loadTemplateReviewSession();
    if (session) {
      setFieldDecisions(session.instrumentDecisions);
      setProcessedInstruments(session.processedInstruments);
    }
  };

  const saveSessionState = useCallback(() => {
    instrumentService.saveTemplateReviewSession({
      instrumentDecisions: fieldDecisions,
      processedInstruments,
      lastActiveTimestamp: Date.now()
    });
  }, [fieldDecisions, processedInstruments]);

  // Save session state whenever decisions change
  useEffect(() => {
    if (Object.keys(fieldDecisions).length > 0) {
      saveSessionState();
    }
  }, [fieldDecisions, processedInstruments, saveSessionState]);

  // ===== FILTERING & SEARCH =====
  const filteredInstruments = useMemo(() => {
    let filtered = instrumentChanges.filter(change => 
      !processedInstruments.includes(change.instrumentId)
    );

    // Apply search filter - search all instrument data
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(change => {
        // Find the full instrument from outdatedInstruments
        const instrument = outdatedInstruments.find(inst => inst.id === change.instrumentId);
        if (!instrument) return false;

        // Search instrument name
        if (change.instrumentName.toLowerCase().includes(query)) return true;
        
        // Search all instrument field values
        const searchableFields = [
          instrument.symbol,
          instrument.displayName,
          instrument.classification.assetClass,
          instrument.classification.subAssetClass,
          instrument.quoteCurrency,
          instrument.contractAsset,
          instrument.contractSize?.toString(),
          instrument.incrementSize?.toString(),
          instrument.incrementType,
          instrument.instrumentReferenceId,
          instrument.notes
        ];

        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [instrumentChanges, searchQuery, processedInstruments, outdatedInstruments]);

  // Determine current bulk state based on field decisions for VISIBLE instruments only
  const bulkState = useMemo(() => {
    // Only look at decisions for instruments that are currently visible (filtered)
    const visibleDecisions = filteredInstruments.flatMap(change => 
      change.fieldChanges.map(field => 
        fieldDecisions[change.instrumentId]?.[field.fieldName] ?? true
      )
    );
    
    if (visibleDecisions.length === 0) return 'accept-all';
    
    const allAccepted = visibleDecisions.every(decision => decision === true);
    const allRejected = visibleDecisions.every(decision => decision === false);
    
    if (allAccepted) return 'accept-all';
    if (allRejected) return 'reject-all';
    return 'custom';
  }, [fieldDecisions, filteredInstruments]);

  // ===== FIELD DECISION HANDLERS =====
  const handleFieldDecision = (instrumentId: string, fieldName: string, shouldAccept: boolean) => {
    setFieldDecisions(prev => ({
      ...prev,
      [instrumentId]: {
        ...prev[instrumentId],
        [fieldName]: shouldAccept
      }
    }));
  };

  // ===== INDIVIDUAL INSTRUMENT COMMIT =====
  const commitInstrumentUpdates = async (instrumentId: string) => {
    try {
      const decisions = fieldDecisions[instrumentId];
      if (!decisions) return;

      // Start fade-out animation
      setProcessingInstruments(prev => new Set([...prev, instrumentId]));
      
      await instrumentService.applyTemplateUpdates(instrumentId, decisions);
      
      // After animation completes, remove from list
      setTimeout(() => {
        setProcessedInstruments(prev => [...prev, instrumentId]);
        setProcessingInstruments(prev => {
          const next = new Set(prev);
          next.delete(instrumentId);
          return next;
        });
      }, 250); // Short delay for fade-out
      
    } catch (error) {
      console.error('Failed to commit instrument updates:', error);
      // Remove from processing state on error
      setProcessingInstruments(prev => {
        const next = new Set(prev);
        next.delete(instrumentId);
        return next;
      });
    }
  };

  // ===== BULK OPERATIONS =====
  const handleBulkAcceptAll = () => {
    const newDecisions = { ...fieldDecisions };
    filteredInstruments.forEach(change => {
      change.fieldChanges.forEach(field => {
        newDecisions[change.instrumentId] = {
          ...newDecisions[change.instrumentId],
          [field.fieldName]: true
        };
      });
    });
    setFieldDecisions(newDecisions);
  };

  const handleBulkRejectAll = () => {
    const newDecisions = { ...fieldDecisions };
    filteredInstruments.forEach(change => {
      change.fieldChanges.forEach(field => {
        newDecisions[change.instrumentId] = {
          ...newDecisions[change.instrumentId],
          [field.fieldName]: false
        };
      });
    });
    setFieldDecisions(newDecisions);
  };

  // ===== COMMIT ALL OPERATION =====
  const handleCommitAll = async () => {
    const instrumentsToCommit = filteredInstruments.filter(
      change => !processingInstruments.has(change.instrumentId)
    );
    
    for (const change of instrumentsToCommit) {
      await commitInstrumentUpdates(change.instrumentId);
      // Small delay between commits to allow animations to stagger
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  };

  const handleSaveAndContinue = () => {
    saveSessionState();
    onInstrumentsUpdated?.(); // Refresh parent when saving progress
    onClose();
  };

  // ===== CLEANUP ON CLOSE =====
  const handleModalClose = () => {
    // Clear session when user closes without saving
    instrumentService.clearTemplateReviewSession();
    onInstrumentsUpdated?.(); // Refresh parent when closing
    onClose();
  };

  // ===== RENDER FIELD DISPLAY =====
  const renderFieldDisplay = (field: FieldChange, instrumentId: string) => {
    const decision = fieldDecisions[instrumentId]?.[field.fieldName] ?? true;
    const isAccept = decision;

    return (
      <div key={field.fieldName} className="flex items-center justify-between py-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground flex items-center gap-2">
            <span>{field.fieldLabel}:</span>
            <span className={isAccept ? "text-text-tertiary line-through" : "text-text-primary font-medium"}>
              {field.currentValue}
            </span>
            <span className="text-foreground-400">→</span>
            <span className={isAccept ? "text-text-primary font-medium" : "text-text-tertiary line-through"}>
              {field.templateValue}
            </span>
          </div>
        </div>
        
        <div className="ml-4">
          <Switch
            size="sm"
            color={isAccept ? "success" : "danger"}
            isSelected={isAccept}
            onValueChange={(checked) => handleFieldDecision(instrumentId, field.fieldName, checked)}
            classNames={{
              base: "flex-row-reverse",
              // wrapper: isAccept? "ml-4 bg-default":"ml-4 bg-default",
              wrapper: "ml-4",
              // thumb: isAccept? "bg-default" : "bg-danger"
            }}
          >
            {isAccept ? "Accept" : "Reject"}
          </Switch>
        </div>
      </div>
    );
  };

  // ===== RENDER INSTRUMENT CARD =====
  const renderInstrumentCard = (change: InstrumentTemplateChanges) => {
    const isProcessing = processingInstruments.has(change.instrumentId);
    
    return (
      <Card 
        key={change.instrumentId} 
        className={`mb-2 transition-all duration-250 ease-out ${
          isProcessing ? 'opacity-0 scale-95 translate-x-4' : ''
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between w-full">
            <h3 className="text-base font-semibold text-foreground">
              {change.instrumentName}
            </h3>
            <Button
              size="sm"
              color="primary"
              variant="ghost"
              onPress={() => commitInstrumentUpdates(change.instrumentId)}
              endContent={<span>💾</span>}
              isDisabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Commit'}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="space-y-1">
            {change.fieldChanges.map(field => 
              renderFieldDisplay(field, change.instrumentId)
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  const remainingCount = filteredInstruments.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      size="2xl"
      scrollBehavior="inside"
      className="max-h-[90vh]"
    >
      <ModalContent>
        <ModalHeader className="w-full">
            <div className="space-y-3 w-full">
              <div>
                <div className="flex items-end justify-between w-full">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground">
                      Template Updates Available
                    </h2>
                    <p className="text-sm text-text-secondary">
                      {remainingCount} instrument{remainingCount !== 1 ? 's' : ''} need{remainingCount === 1 ? 's' : ''} review
                    </p>
                  </div>
                  
                  {remainingCount > 0 && (
                    <ButtonGroup
                      options={[
                        { value: 'reject-all', label: 'Reject All' },
                        { value: 'accept-all', label: 'Accept All' },
                        { value: 'custom', label: 'Custom' }
                      ]}
                      value={bulkState}
                      onChange={(value) => {
                        if (value === 'accept-all') {
                          handleBulkAcceptAll();
                        } else if (value === 'reject-all') {
                          handleBulkRejectAll();
                        }
                        // 'custom' doesn't trigger an action, just shows current state
                      }}
                      variant="flat"
                      size="sm"
                      selectedColor={bulkState === 'accept-all' ? 'success' : bulkState === 'reject-all' ? 'warning' : 'primary'}
                    />
                  )}
                </div>
              </div>
              
              <Input
                placeholder="🔍 Search instruments or fields..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                size="sm"
              />
            </div>
        </ModalHeader>

        <ModalBody>
              {loading ? (
                <div className="text-center py-8">
                  <div className="text-lg">Loading instrument changes...</div>
                </div>
              ) : remainingCount === 0 ? (
                <div className="text-center py-8">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="text-lg font-medium">All instruments up to date!</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredInstruments.map(change => 
                    renderInstrumentCard(change)
                  )}
                </div>
              )}
        </ModalBody>

        <ModalFooter className="justify-between">
          <Button 
            color="default" 
            variant="bordered"
            onPress={handleSaveAndContinue}
          >
            Save & Continue Later
          </Button>
          
          <Button 
            color="primary"
            variant="solid"
            onPress={handleCommitAll}
            isDisabled={remainingCount === 0 || processingInstruments.size > 0}
            endContent={<span>💾</span>}
          >
            Commit All ({remainingCount})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TemplateReviewModal;